import type { PluginAccessControl, PluginDefinition, PluginFeaturePermission, RoleLevel } from '@/plugins/types';

/**
 * Numerische Rollen-Hierarchie.
 * Identisch zu Backend: OrganizationRole.cs / WorkspaceRole.cs
 */
const ROLE_HIERARCHY: Record<RoleLevel, number> = {
  Reader: 0,
  Editor: 1,
  Reviewer: 2,
  Admin: 3,
  SuperAdmin: 4,
} as const;

/**
 * Prüft ob `userRole` mindestens `requiredRole` erreicht.
 * Null/undefined userRole → false (sicher bei noch nicht geladenem User).
 */
export function isAtLeast(userRole: string | null | undefined, requiredRole: RoleLevel): boolean {
  if (!userRole) return false;
  const userLevel = ROLE_HIERARCHY[userRole as RoleLevel];
  const requiredLevel = ROLE_HIERARCHY[requiredRole];
  if (userLevel === undefined || requiredLevel === undefined) return false;
  return userLevel >= requiredLevel;
}

/**
 * Löst accessControl für ein Plugin auf.
 * Backward-Compatible: Wenn adminOnly auf einem Nav-Item gesetzt war,
 * wird das als { minOrgRole: 'Admin' } interpretiert.
 */
export function resolveAccessControl(plugin: PluginDefinition): PluginAccessControl | undefined {
  if (plugin.accessControl) return plugin.accessControl;

  // Backward-Compat: Wenn ein Nav-Item adminOnly hat, leiten wir accessControl ab
  const hasAdminOnlyNav = plugin.navigation?.some((nav) => nav.adminOnly);
  if (hasAdminOnlyNav) {
    return { minOrgRole: 'Admin' };
  }

  return undefined;
}

/**
 * Prüft ob ein User auf ein Plugin zugreifen darf.
 * Ohne accessControl → alle dürfen.
 */
export function canAccessPlugin(
  plugin: PluginDefinition,
  orgRole: string | null | undefined,
  workspaceRole?: string | null,
): boolean {
  const ac = resolveAccessControl(plugin);
  if (!ac) return true;

  if (ac.minOrgRole && !isAtLeast(orgRole, ac.minOrgRole)) return false;

  // Workspace role check: when workspaceRole is null/undefined (not yet resolved),
  // Org-Admins are allowed through (they have implicit access to all workspaces).
  // When a concrete workspaceRole is provided, it must meet the minimum.
  if (ac.minWorkspaceRole) {
    if (workspaceRole != null) {
      if (!isAtLeast(workspaceRole, ac.minWorkspaceRole)) return false;
    } else if (!isAtLeast(orgRole, 'Admin')) {
      return false;
    }
  }

  return true;
}

/**
 * Prüft ob der User das Plugin nur im Read-Only-Modus nutzen darf.
 */
export function isReadOnlyAccess(
  plugin: PluginDefinition,
  orgRole: string | null | undefined,
): boolean {
  const ac = resolveAccessControl(plugin);
  if (!ac?.readOnlyForOrgRoles) return false;
  if (!orgRole) return false;
  return ac.readOnlyForOrgRoles.includes(orgRole as RoleLevel);
}

/**
 * Prüft ob ein User ein bestimmtes Feature eines Plugins nutzen darf.
 */
export function canUseFeature(
  plugin: PluginDefinition,
  featureId: string,
  orgRole: string | null | undefined,
  workspaceRole?: string | null,
): boolean {
  const ac = resolveAccessControl(plugin);
  if (!ac?.features) return true;

  const featurePerm: PluginFeaturePermission | undefined = ac.features[featureId];
  if (!featurePerm) return true;

  if (featurePerm.minOrgRole && !isAtLeast(orgRole, featurePerm.minOrgRole)) return false;

  // DevPlan 0139/T2: Org-Admin-Bypass konsistent zu canAccessPlugin — wenn die
  // WS-Rolle (noch) nicht aufgelöst ist (null), kommt ein Org-Admin durch
  // (impliziter Zugriff auf alle Workspaces seiner Org). Bei konkret aufgelöster
  // WS-Rolle gilt das Minimum (kein Org→WS-Ceiling-Bypass). Server bleibt autoritativ.
  if (featurePerm.minWorkspaceRole) {
    if (workspaceRole != null) {
      if (!isAtLeast(workspaceRole, featurePerm.minWorkspaceRole)) return false;
    } else if (!isAtLeast(orgRole, 'Admin')) {
      return false;
    }
  }

  return true;
}
